import { http, HttpResponse, delay } from 'msw';
import { mockActivities, generateMockActivity } from './data/mockActivities';
import { Activity } from '@/shared/state/activityStore';

const API_BASE = 'http://localhost:3000/api';

// Simulate API delay (300-800ms)
const simulateDelay = () => delay(Math.random() * 500 + 300);

// In-memory storage for mock data
let activities = [...mockActivities];
let photos: any[] = [];

export const handlers = [
  // Auth endpoints
  // TEST CREDENTIALS: test@example.com / Password123!
  http.post(`${API_BASE}/auth/login`, async ({ request }) => {
    await simulateDelay();
    const body = await request.json() as any;

    console.log('🔐 Login attempt:', { email: body.email, password: body.password });

    // Hardcoded test credentials
    if (body.email === 'test@example.com' && body.password === 'Password123!') {
      console.log('✅ Login successful');
      return HttpResponse.json({
        user: {
          id: '1',
          email: body.email,
          name: 'Test User',
          avatar: '/placeholder.svg',
          role: 'farmer',
        },
        accessToken: 'mock-access-token',
        refreshToken: 'mock-refresh-token',
      });
    }

    // Invalid credentials
    console.log('❌ Invalid credentials');
    return HttpResponse.json(
      { message: 'Invalid email or password. Use test@example.com / Password123!' },
      { status: 401 }
    );
  }),

  http.post(`${API_BASE}/auth/register`, async ({ request }) => {
    await simulateDelay();
    const body = await request.json() as any;

    return HttpResponse.json({
      user: {
        id: '1',
        email: body.email,
        name: body.name,
        avatar: '/placeholder.svg',
        role: 'farmer',
      },
      accessToken: 'mock-access-token',
      refreshToken: 'mock-refresh-token',
    });
  }),

  http.post(`${API_BASE}/auth/logout`, async () => {
    await simulateDelay();
    return HttpResponse.json({ success: true });
  }),

  http.get(`${API_BASE}/auth/me`, async () => {
    await simulateDelay();
    return HttpResponse.json({
      id: '1',
      email: 'user@example.com',
      name: 'Test User',
      avatar: '/placeholder.svg',
      role: 'farmer',
    });
  }),

  // Activities endpoints
  http.get(`${API_BASE}/activities`, async () => {
    await simulateDelay();
    return HttpResponse.json(activities);
  }),

  http.get(`${API_BASE}/activities/:id`, async ({ params }) => {
    await simulateDelay();
    const activity = activities.find((a) => a.id === params.id);
    
    if (!activity) {
      return HttpResponse.json(
        { message: 'Activity not found' },
        { status: 404 }
      );
    }

    return HttpResponse.json(activity);
  }),

  http.post(`${API_BASE}/activities`, async ({ request }) => {
    await simulateDelay();
    const body = await request.json() as Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>;

    const newActivity: Activity = {
      ...body,
      id: `${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    activities = [newActivity, ...activities];
    return HttpResponse.json(newActivity, { status: 201 });
  }),

  http.patch(`${API_BASE}/activities/:id`, async ({ params, request }) => {
    await simulateDelay();
    const body = await request.json() as Partial<Activity>;
    
    const index = activities.findIndex((a) => a.id === params.id);
    if (index === -1) {
      return HttpResponse.json(
        { message: 'Activity not found' },
        { status: 404 }
      );
    }

    activities[index] = {
      ...activities[index],
      ...body,
      updatedAt: new Date().toISOString(),
    };

    return HttpResponse.json(activities[index]);
  }),

  http.delete(`${API_BASE}/activities/:id`, async ({ params }) => {
    await simulateDelay();
    const index = activities.findIndex((a) => a.id === params.id);
    
    if (index === -1) {
      return HttpResponse.json(
        { message: 'Activity not found' },
        { status: 404 }
      );
    }

    activities = activities.filter((a) => a.id !== params.id);
    return HttpResponse.json({ success: true });
  }),

  // Photos endpoints
  http.get(`${API_BASE}/photos`, async ({ request }) => {
    await simulateDelay();
    const url = new URL(request.url);
    const activityId = url.searchParams.get('activityId');

    if (activityId) {
      return HttpResponse.json(
        photos.filter((p) => p.activityId === activityId)
      );
    }

    return HttpResponse.json(photos);
  }),

  http.post(`${API_BASE}/photos`, async ({ request }) => {
    await simulateDelay();
    const body = await request.json() as any;

    const newPhoto = {
      ...body,
      id: `${Date.now()}`,
      thumbnail: body.url,
      uploadedAt: new Date().toISOString(),
      tags: body.tags || [],
    };

    photos = [newPhoto, ...photos];
    return HttpResponse.json(newPhoto, { status: 201 });
  }),

  http.delete(`${API_BASE}/photos/:id`, async ({ params }) => {
    await simulateDelay();
    photos = photos.filter((p) => p.id !== params.id);
    return HttpResponse.json({ success: true });
  }),

  // File upload endpoint
  http.post(`${API_BASE}/uploads`, async () => {
    await delay(1000); // Simulate longer upload time
    return HttpResponse.json({
      url: '/placeholder.svg',
      id: `file-${Date.now()}`,
    });
  }),

  // Score endpoints
  http.get(`${API_BASE}/score`, async () => {
    await simulateDelay();
    return HttpResponse.json({
      totalPoints: 1250,
      level: 2,
      streak: 5,
      rank: 'Growing Farmer',
      rankProgress: 0.25,
      history: [],
      achievements: [],
    });
  }),

  http.get(`${API_BASE}/score/history`, async () => {
    await simulateDelay();
    return HttpResponse.json(
      Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString(),
        points: Math.floor(Math.random() * 100) + 20,
      }))
    );
  }),

  http.get(`${API_BASE}/leaderboard`, async () => {
    await simulateDelay();
    return HttpResponse.json(
      Array.from({ length: 10 }, (_, i) => ({
        rank: i + 1,
        userId: `${i + 1}`,
        userName: `Farmer ${i + 1}`,
        avatar: '/placeholder.svg',
        score: 5000 - i * 500,
      }))
    );
  }),
];
